import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCog, TrendingUp, Wallet, Flame, Filter } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const LABEL_STYLE = {
  hot: { color: "bg-rose-500 text-white", text: "🔥 Hot" },
  warm: { color: "bg-amber-500 text-white", text: "Warm" },
  cold: { color: "bg-slate-400 text-white", text: "Cold" },
};

export default function Investors() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tags, setTags] = useState([]);
  const [tagFilter, setTagFilter] = useState("");

  useEffect(() => {
    const params = tagFilter ? { tag: tagFilter } : {};
    api.get("/investors", { params }).then(({ data }) => setItems(data));
    api.get("/investors/tags").then(({ data }) => setTags(data)).catch(() => {});
  }, [tagFilter]);

  const filtered = items.filter((i) => !q || (i.name + " " + (i.email || "") + " " + (i.company || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="investors-page">
      <header>
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-primary" />
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Investitori</h1>
        </div>
        <p className="text-muted-foreground text-sm mt-1">Rete di investitori con Investor Matching Score aggregato.</p>
      </header>

      <Card className="border-border/60 p-4 space-y-3">
        <Input placeholder="Cerca per nome, azienda, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="investors-search" />
        {tags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Filter className="w-3 h-3" />Tag:</span>
            <button onClick={() => setTagFilter("")}
              className={`text-xs px-2 py-1 rounded-md border ${!tagFilter ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-muted"}`}
              data-testid="tag-all">Tutti</button>
            {tags.map((t) => (
              <button key={t.tag} onClick={() => setTagFilter(t.tag)}
                className={`text-xs px-2 py-1 rounded-md border ${tagFilter === t.tag ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-muted"}`}
                data-testid={`tag-${t.tag}`}>{t.tag} <span className="opacity-70 tabular-nums">{t.count}</span></button>
            ))}
          </div>
        )}
      </Card>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Nessun investitore. Crea un contatto con ruolo investitore/acquirente.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const s = c._score || {};
            const lbl = LABEL_STYLE[s.label] || LABEL_STYLE.cold;
            return (
              <Link key={c.id} to={`/investors/${c.id}`} className="block group" data-testid={`inv-card-${c.id}`}>
                <Card className="border-border/60 p-5 hover:border-primary/60 hover:shadow-sm transition-all h-full">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="font-heading font-semibold group-hover:text-primary truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.company || c.email || c.phone}</div>
                    </div>
                    <Badge className={`${lbl.color} border-transparent text-[10px] shrink-0`}>{lbl.text}</Badge>
                  </div>

                  {/* Investor Matching Score */}
                  <div className="bg-muted/60 rounded-md p-3 mb-3">
                    <div className="flex items-baseline gap-2">
                      <span className="font-heading text-3xl font-bold tabular-nums">{s.total_score ?? "—"}</span>
                      <span className="text-xs text-muted-foreground">/100</span>
                      <span className="ml-auto text-[10px] text-muted-foreground">Investor Matching Score</span>
                    </div>
                    <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                      <span>Fit <span className="text-foreground font-semibold tabular-nums">{s.fit_score ?? "—"}</span></span>
                      <span>Engagement <span className="text-foreground font-semibold tabular-nums">{s.engagement_score ?? "—"}</span></span>
                      <span>Match <span className="text-foreground font-semibold tabular-nums">{s.matches_count ?? "—"}</span></span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-muted rounded-md p-2">
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider flex items-center gap-1"><Wallet className="w-3 h-3" />Budget</div>
                      <div className="font-medium tabular-nums truncate">{c.investor_budget_max ? `€ ${fmt(c.investor_budget_min || 0)}–${fmt(c.investor_budget_max)}` : "—"}</div>
                    </div>
                    <div className="bg-muted rounded-md p-2">
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider flex items-center gap-1"><TrendingUp className="w-3 h-3" />ROI min</div>
                      <div className="font-medium tabular-nums">{c.investor_roi_min ? `${c.investor_roi_min}%` : "—"}</div>
                    </div>
                  </div>
                  {(c.tags?.length > 0 || c.investor_cities?.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(c.tags || []).slice(0, 3).map((t) => <Badge key={t} className="bg-primary/10 text-primary border-transparent text-[10px]">🏷 {t}</Badge>)}
                      {(c.investor_cities || []).slice(0, 3).map((city) => <Badge key={city} variant="secondary" className="text-[10px]">{city}</Badge>)}
                    </div>
                  )}
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
function fmt(n) { return Intl.NumberFormat("it-IT").format(Math.round(n || 0)); }
